import * as React from "react";
import { AppBar, Box, createStyles, makeStyles, Tab } from "@material-ui/core";
import {
  BarChart as BarChartIcon,
  PieChartOutlined as PieChartOutlinedIcon,
} from "@material-ui/icons";
import { TabContext, TabList, TabPanel } from "@material-ui/lab";

import { SourceBreakdown } from "components";
import { Source, TopEarningsReport } from "services/valuation/types";
import theme from "theme";
import { REGULAR_CHART_COLOR_LIST } from "theme/constants";

import TopEarningsByYear from "./TopEarningsByYear";

const useStyles = makeStyles(() =>
  createStyles({
    financialChartWrapper: {
      maxWidth: "500px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "column",
      backgroundColor: theme.custom.palette.gray.light,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
      [theme.breakpoints.down("md")]: {
        textAlign: "left",
        width: "100%",
      },
    },
    barChartsUnavailable: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      padding: theme.spacing(2),
    },
    tabRoot: {
      boxShadow: "none",
    },
    tabList: {
      backgroundColor: theme.custom.palette.gray.main,
      color: theme.custom.palette.gray.light,
      "& :hover": {
        color: theme.custom.palette.purple.main,
      },
    },
    tab: {
      minWidth: "50%",
      "&.Mui-selected": {
        color: theme.custom.palette.purple.main,
        backgroundColor: theme.custom.palette.gray.light,
        boxShadow:
          "0px -1px 5px 0px rgba(0,0,0,0.2), 1px 0px 5px 0px rgba(0,0,0,0.2), -1px 0px 5px 0px rgba(0,0,0,0.2)",
      },
    },
    indicator: {
      display: "none",
    },
    barChartTabPanel: {
      height: "500px",
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingBottom: "0px",
      paddingLeft: theme.spacing(3),
    },
    pieChartTabPanel: {
      minHeight: "500px",
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(3),
    },
    paper: { boxShadow: "none" },
  }),
);

interface ValuationChartsProps {
  sources: Source[];
  title: string;
  totalValue: number;
  kind: string;
  barChartData: TopEarningsReport[] | null;
}
const ValuationCharts: React.FC<ValuationChartsProps> = React.memo(
  ({ sources, title, totalValue, kind, barChartData }) => {
    const hasTopEarningsByYear = !!barChartData;

    const [selectedTab, setSelectedTab] = React.useState(
      hasTopEarningsByYear ? "topEarningsByYear" : "ltmBreakdown",
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleChange = (_event: any, value: any) => {
      setSelectedTab(value);
    };
    const classes = useStyles();

    return (
      <Box className={classes.financialChartWrapper}>
        {hasTopEarningsByYear ? (
          <div className={classes.tabRoot}>
            <TabContext value={selectedTab}>
              <AppBar position="static" className={classes.paper}>
                <TabList
                  indicatorColor="primary"
                  textColor="primary"
                  aria-label="disabled tabs example"
                  variant="fullWidth"
                  value={selectedTab}
                  onChange={handleChange}
                  classes={{ indicator: classes.indicator }}
                  className={classes.tabList}
                >
                  <Tab
                    icon={<BarChartIcon />}
                    className={classes.tab}
                    value="topEarningsByYear"
                  />
                  <Tab
                    icon={<PieChartOutlinedIcon />}
                    className={classes.tab}
                    value="ltmBreakdown"
                  />
                </TabList>
              </AppBar>
              <TabPanel
                value="topEarningsByYear"
                className={classes.barChartTabPanel}
              >
                <TopEarningsByYear title={title} data={barChartData} />
              </TabPanel>
              <TabPanel
                value="ltmBreakdown"
                className={classes.pieChartTabPanel}
              >
                <SourceBreakdown
                  sources={sources}
                  colorList={REGULAR_CHART_COLOR_LIST}
                  title={title}
                  subtitle="Last 12 Months Earnings"
                  totalValue={totalValue}
                  key={`${kind}-chart`}
                  pieChartHeight={180}
                />
              </TabPanel>
            </TabContext>
          </div>
        ) : (
          <Box className={classes.pieChartTabPanel}>
            <SourceBreakdown
              sources={sources}
              colorList={REGULAR_CHART_COLOR_LIST}
              title={title}
              subtitle="Last 12 Months Earnings"
              totalValue={totalValue}
              key={`${kind}-chart`}
              pieChartHeight={180}
            />
          </Box>
        )}
      </Box>
    );
  },
);
export default ValuationCharts;
